/**
 * Come si ricontatta una persona a partire da quello che ha scritto nel
 * modulo, e come si mostra nell'elenco. Il modulo non salva che tipo di testo
 * fosse (email, telefono, libero): si riconosce dal valore.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Cifre, spazi, punti, trattini, parentesi e un "+" iniziale: lo stesso
// controllo del modulo nel widget.
const TELEFONO = /^\+?\d{6,15}$/;
const pulisci = (testo) => testo.replace(/[\s.\-()/]/g, "");

const testi = (contact) =>
  (contact?.values || []).filter((v) => typeof v.value === "string" && v.value.trim());

export const isEmail = (testo) => typeof testo === "string" && EMAIL.test(testo.trim());
export const isTelefono = (testo) => typeof testo === "string" && TELEFONO.test(pulisci(testo.trim()));

/** La prima email lasciata, o "". */
export function emailDi(contact) {
  return testi(contact).find((v) => isEmail(v.value))?.value.trim() || "";
}

/** Il primo telefono lasciato, com'e' stato scritto, o "". */
export function telefonoDi(contact) {
  return testi(contact).find((v) => !isEmail(v.value) && isTelefono(v.value))?.value.trim() || "";
}

/** Il numero per il link tel:, solo cifre e "+". */
export const telLink = (telefono) => `tel:${pulisci(telefono)}`;

/**
 * Il numero per wa.me: cifre con il prefisso internazionale, o "" se non si
 * puo' sapere. Chi scrive in un modulo italiano spesso lo omette: un
 * cellulare italiano (10 cifre che iniziano per 3) prende il +39; per gli
 * altri numeri senza prefisso il paese non si indovina e il pulsante non c'e'.
 */
export function whatsappDa(telefono) {
  const cifre = pulisci(telefono || "");
  if (/^\+\d{8,15}$/.test(cifre)) return cifre.slice(1);
  if (/^00\d{8,15}$/.test(cifre)) return cifre.slice(2);
  if (/^3\d{9}$/.test(cifre)) return `39${cifre}`;
  return "";
}

/** Il nome, se la persona l'ha lasciato: il titolo del contatto se non e' un recapito. */
export function nomeDi(contact) {
  const titolo = String(contact?.headline || "").trim();
  if (!titolo || isEmail(titolo) || isTelefono(titolo) || titolo === "Contatto senza dati") return "";
  return titolo;
}

/** Le iniziali per il tondo accanto al nome: due lettere, o la prima del recapito. */
export function iniziali(contact) {
  const nome = nomeDi(contact);
  if (nome) {
    const parole = nome.split(/\s+/).filter((p) => /\p{L}/u.test(p));
    const lettere = parole.slice(0, 2).map((p) => p.match(/\p{L}/u)[0]);
    if (lettere.length) return lettere.join("").toUpperCase();
  }
  const primo = String(contact?.headline || "").match(/\p{L}|\d/u);
  return primo ? primo[0].toUpperCase() : "?";
}

const giorno = (data) => new Date(data.getFullYear(), data.getMonth(), data.getDate());
const GIORNO_MS = 24 * 60 * 60 * 1000;

/** Quanti giorni di calendario fa, rispetto a `ora` (0 = oggi). */
function giorniFa(data, ora) {
  return Math.round((giorno(ora) - giorno(data)) / GIORNO_MS);
}

/** Il gruppo dell'elenco: Oggi, Ieri, Questa settimana, poi il mese. */
export function gruppoDi(valore, ora = new Date()) {
  const data = new Date(valore);
  const fa = giorniFa(data, ora);
  if (fa <= 0) return "Oggi";
  if (fa === 1) return "Ieri";
  if (fa < 7) return "Questa settimana";
  const mese = data.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  return mese.charAt(0).toUpperCase() + mese.slice(1);
}

/** L'ora breve accanto al nome: 10:32 oggi, "ieri", il giorno della settimana, poi la data. */
export function quandoBreve(valore, ora = new Date()) {
  const data = new Date(valore);
  const fa = giorniFa(data, ora);
  if (fa <= 0) return data.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  if (fa === 1) return "ieri";
  if (fa < 7) return data.toLocaleDateString("it-IT", { weekday: "short" });
  const stessoAnno = data.getFullYear() === ora.getFullYear();
  return data.toLocaleDateString("it-IT", { day: "numeric", month: "short", ...(stessoAnno ? {} : { year: "2-digit" }) });
}

/** La data per esteso nella scheda: "23 settembre 2026 alle 10:32". */
export function quandoEsteso(valore) {
  const data = new Date(valore);
  const giornoTesto = data.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
  const oraTesto = data.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  return `${giornoTesto} alle ${oraTesto}`;
}

/** La pagina da cui ha scritto, leggibile: il titolo, o dominio e percorso. */
export function paginaLeggibile(context) {
  if (context?.page_title) return context.page_title;
  try {
    const url = new URL(context?.page_url);
    return `${url.hostname}${url.pathname === "/" ? "" : url.pathname}`;
  } catch {
    return context?.page_url || "";
  }
}
